import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const erpnextSection: DocsSection = {
  label: "ERPNext (ERP)",
  pages: [
    {
      slug: "erpnext-configured",
      title: "What's configured",
      description: "The Setup Wizard / Israel-pack baseline.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: "The baseline is created by the ERPNext Setup Wizard plus the Israel country pack on first login — NOT by custom code. It establishes:",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            "Company and Fiscal Year 2026.",
            "Chart of Accounts.",
            ["VAT 18% + Israel tax templates."],
            "Standard price lists (ILS).",
            "Opportunity Types / Sales Stages.",
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: "Company name differs per env",
          text: [
            "dev = ",
            { code: "Moveo" },
            " (and a ",
            { code: "Moveo (Demo)" },
            " company); staging = ",
            { code: "Yango" },
            ". Identity reconciliation is in progress — treat the default company as a runtime lookup, not a constant.",
          ],
        },
      ],
    },
    {
      slug: "erpnext-customizations",
      title: "Customizations",
      description: "What yango-customizations adds, and where it shows on the desk.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Customizations ship in the ",
            { code: "yango-customizations" },
            " release (currently ",
            { code: "0.1.2" },
            "), baked into the ERPNext image. What it adds, and where to see it on the desk:",
          ],
        },
        {
          kind: "table",
          headers: ["Customization", "Where on the desk"],
          rows: [
            [
              [
                { strong: "Hebrew-first desk" },
                " — Hebrew is the system default language; RTL is automatic by language code.",
              ],
              [
                { code: "/app/language" },
                " → ",
                { code: "he" },
                " Enabled. Flip via My Settings → Language → עברית.",
              ],
            ],
            [
              [{ strong: "B2B UOMs" }, " — Carton / Layer / Pallet."],
              [{ code: "/app/uom" }, " (and in any Item's UOM dropdown)."],
            ],
            [
              [{ strong: "Warehouse nodes" }, " — Merlog Receiving + B2B Dark Store."],
              [{ code: "/app/warehouse/view/tree" }, ' under "All Warehouses - <abbr>".'],
            ],
          ],
        },
      ],
    },
    {
      slug: "erpnext-writing-customizations",
      title: "Writing customizations",
      description: "The after_migrate setter pattern and v16 gotchas.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            { code: "yango-customizations" },
            " is an ",
            { strong: "additive" },
            " Frappe app, organized modules-per-area (",
            { code: "stock_ext" },
            ", ",
            { code: "locale_ext" },
            ", ",
            { code: "buying_ext" },
            "…), baked onto official ",
            { code: "frappe/erpnext" },
            " via ",
            { code: "apps.json" },
            ". It ",
            { strong: "never edits core" },
            ".",
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Config delivery = idempotent after_migrate setters",
          text: "Deliver config with create-if-not-exists after_migrate setters — NOT fixtures for Singles or heavy masters. Resolve the default company at runtime; don't hardcode it.",
        },
        {
          kind: "heading",
          id: "v16-gotchas",
          text: "v16 gotchas (each cost a real bug)",
        },
        {
          kind: "table",
          headers: ["Gotcha", "Detail"],
          rows: [
            [{ code: "UOM" }, ["The DocType is ", { code: "UOM" }, ", not ", { code: "UoM" }, "."]],
            [
              "Numbered CoA",
              [
                '"Standard with Numbers" numbers account names — resolve groups dynamically, don\'t hardcode names.',
              ],
            ],
            ["Language RTL", ["Language has no RTL field — RTL is by language code."]],
            ["Lead Source", [{ code: "Lead Source" }, " doesn't exist in v16."]],
            ["CRM dedup field", ["It's ", { code: "allow_lead_duplication_based_on_emails" }, "."]],
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "Always validate against a live local ERPNext",
          text: [
            "Run ",
            { code: "bench migrate" },
            " on a live local stack before merge. Setters log-and-skip on failure, so a deploy can ",
            { strong: "succeed silently" },
            " with missing config.",
          ],
        },
      ],
    },
    {
      slug: "erpnext-returns-credits",
      title: "Returns, credits & refunds",
      description:
        "How a B2B return becomes a credit (EZCount 330) or a refund — and how to raise one.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "A credit memo is the Israeli ",
            { strong: "חשבונית זיכוי" },
            " — EZCount document ",
            { code: "type 330" },
            ". It reverses value on an already-issued invoice, whole or in part. A B2B return settles one of two ways depending on the customer's billing track: an ",
            { strong: "immediate / card" },
            " customer is refunded to the card (out of obligo — the return→card-refund bridge is in review, not yet live); a ",
            { strong: "monthly" },
            " customer's credit rides as a negative line on the month-end consolidated invoice (305) and reduces obligo.",
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "The business decides; service executes",
          text: [
            "Whether to credit is a business decision — the platform only executes it. Execution authority splits by track: ",
            { strong: "monthly" },
            " credits are Finance-only and need account-manager approval; ",
            { strong: "immediate" },
            " credits are run by customer service (Bat-Sheva's team) besides Finance. Amount-based approval thresholds are still an open business decision (R203).",
          ],
        },
        {
          kind: "heading",
          id: "flow-at-a-glance",
          text: "The flow at a glance",
        },
        {
          kind: "mermaid",
          eyebrow: "credit-memo flow",
          title: "Return → 330 → customer balance",
          caption:
            "A return or adjustment issues a 330 credit; both billing tracks converge on the customer balance.",
          fallbackHeight: 340,
          chart: `flowchart TD
  R(["Return raised — the business authorises the credit"]) --> T{"Which trigger?"}
  T -- "Return Delivery Note" --> SG["Shortfall gate (BF-636) · manager approval at or above threshold"]
  T -- "B2B Return" --> ENG["issue_ezcount_credit"]
  T -- "B2B Credit Adjustment" --> ENG
  SG --> ENG
  ENG --> B{"Billing track?"}
  B -- "immediate / card" --> IMM["330 issued now (card refund via HYP · in review) · OUT of obligo"]
  B -- "monthly" --> MON["credit mirror parked (no standalone 330) → nets into month-end 305 · obligo reduced"]
  IMM --> BAL(["Customer balance · Owed goes down"])
  MON --> BAL
  classDef proc fill:#E0F7F7,stroke:#00A0A0,color:#1A2535;
  classDef term fill:#F8F4ED,stroke:#C9A96A,color:#1A2535;
  classDef bal fill:#FFE7E7,stroke:#E08A8A,color:#1A2535;
  class SG,ENG,IMM,MON proc;
  class R term;
  class BAL bal;`,
        },
        {
          kind: "table",
          headers: ["Trigger", "Produces", "Activated by"],
          rows: [
            [
              [{ strong: "Return Delivery Note" }, " — submit an ", { code: "is_return" }, " DN"],
              ["A return 200 (", { code: "תעודת משלוח" }, ") + a 330 for immediate clients"],
              [
                "The base issuance config (see ",
                { href: "#enabling-the-flow", text: "Enabling the flow" },
                "); governed by the shortfall gate",
              ],
            ],
            [
              [{ strong: "B2B Return doc" }, " — whole / partial / one-item / part-of-item"],
              "Monetary Credit → 330; Physical Pickup → ToDo; Supplier → Purchase Receipt",
              [{ code: "return_orchestration_enabled" }, " in EZCount Settings"],
            ],
            [
              [{ strong: "B2B Credit Adjustment" }],
              "Partial credit, cancel & reissue, or re-charge",
              [{ code: "credit_mechanics_enabled" }, " in EZCount Settings"],
            ],
            [
              [{ strong: "Shortfall gate" }],
              "Nothing — a manager-approval gate fronting the return DN",
              [
                "The ",
                { code: "b2b_shortfall_credit_threshold" },
                " + manager role (a ",
                { code: "0.0" },
                " threshold blocks non-managers)",
              ],
            ],
            [
              [{ strong: "Card refund" }, " (immediate)"],
              "A payment-side HYP refund, separate from the 330",
              [{ code: "apps/api" }, " payments (return → refund bridge)"],
            ],
          ],
        },
        {
          kind: "heading",
          id: "how-a-330-is-issued",
          text: "How a 330 reaches EZCount",
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "Submitting the return DN enqueues a background job (",
              { code: "issue_ezcount_credit" },
              ", RQ, after commit) — the outbound call is never made inline in the web request.",
            ],
            [
              "The job builds the 330 body (line items, customer ",
              { code: "ח.פ." },
              ", billing address, VAT number) and posts once to ",
              { code: "POST /api/createDoc" },
              ".",
            ],
            [
              "On success the EZCount legal number + PDF link are written back onto the return mirror Sales Invoice (",
              { code: "custom_ezcount_doc_number" },
              " / ",
              { code: "custom_ezcount_pdf_link" },
              ").",
            ],
            [
              "The mirror SI stays a draft (zero-GL) — the 330 is a legal EZCount document, not an ERPNext ledger posting.",
            ],
          ],
        },
        {
          kind: "paragraph",
          text: [
            "Retries are safe: an atomic status CAS on the mirror plus idempotency markers mean a re-run adopts the existing 330 rather than issuing a second one. For a ",
            { strong: "monthly" },
            " client the return DN issues no standalone 330 — the credit mirror is parked and consolidated into the month-end 305 instead.",
          ],
        },
        {
          kind: "heading",
          id: "enabling-the-flow",
          text: "Enabling the flow",
        },
        {
          kind: "paragraph",
          text: [
            "The credit options are activated by configuration — pure ",
            { code: "EZCount Settings" },
            " flags plus a couple of ",
            { code: "site_config" },
            " keys. There is no environment or code lock: switch these on and the flow runs end to end against the EZCount ",
            { strong: "demo" },
            " endpoint (confirm the two first-use behaviours flagged in the callout at the end). Turn on:",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              { code: "EZCount Settings" },
              " (",
              { code: "/app/ezcount-settings" },
              ") → tick ",
              { strong: "Enabled" },
              " (the master connector), ",
              { strong: "Return Orchestration Enabled" },
              ", and ",
              { strong: "Credit Mechanics Enabled" },
              "; set ",
              { code: "developer_email" },
              " and confirm ",
              { code: "base_url = https://demo.ezcount.co.il" },
              ".",
            ],
            [
              { code: "site_config" },
              " → ",
              { code: "ezcount_allow_demo = true" },
              " (an SSRF guard blocks the demo host without it): ",
              { code: "bench --site <site> set-config ezcount_allow_demo true" },
              ".",
            ],
            [
              "A real EZCount ",
              { strong: "demo" },
              " API key in ",
              { code: "infra/compose/.env" },
              " → ",
              { code: "EZCOUNT_API_KEY" },
              " (the placeholder 401s).",
            ],
            [
              "Shortfall gate: act as a manager role (",
              { code: "Accounts Manager" },
              " / ",
              { code: "System Manager" },
              ") or raise ",
              { code: "b2b_shortfall_credit_threshold" },
              " (default ",
              { code: "0.0" },
              " blocks non-managers).",
            ],
            [
              "The RQ ",
              { code: "queue-short" },
              " / ",
              { code: "queue-long" },
              " / ",
              { code: "scheduler" },
              " workers must be running — issuance is a background job (restart them after code changes).",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Desk paths are ERPNext routes",
          text: [
            "The ",
            { code: "/app/…" },
            " links below open on your ",
            { strong: "ERPNext" },
            " host (the desk), not on this docs site.",
          ],
        },
        {
          kind: "heading",
          id: "how-to-use",
          text: "How to use each option",
        },
        {
          kind: "heading",
          id: "use-return-dn",
          text: "Option 1 · Return Delivery Note",
        },
        {
          kind: "paragraph",
          text: [
            "Use it to credit goods a customer sends back. For an ",
            { strong: "immediate" },
            " client it issues a real 330 to EZCount now; for a ",
            { strong: "monthly" },
            " client it parks the credit for the month-end 305.",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "Open the submitted Delivery Note for the delivered order — Desk ",
              { code: "/app/delivery-note" },
              ".",
            ],
            [
              "Create a return against it (the ",
              { code: "Is Return" },
              " checkbox), keeping the lines/quantities being returned.",
            ],
            "On submit, the shortfall gate runs: a non-manager is blocked when the credit amount meets the threshold; a manager approves it.",
            "The system issues the return 200 and, for an immediate client, a 330 credit note to EZCount (background job).",
            [
              "Confirm the EZCount number + PDF link appear on the return mirror Sales Invoice (",
              { code: "custom_ezcount_*" },
              " fields).",
            ],
          ],
        },
        {
          kind: "paragraph",
          text: [
            { strong: "Where & links: " },
            "Desk ",
            { code: "/app/delivery-note" },
            " · see ",
            { href: "#how-a-330-is-issued", text: "How a 330 reaches EZCount" },
            " and the ",
            { href: "#flow-at-a-glance", text: "flow diagram" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "use-b2b-return",
          text: "Option 2 · B2B Return doc",
        },
        {
          kind: "paragraph",
          text: [
            "The orchestrated return. Choose the grain — whole order / partial / one item / part of an item — and it routes the outcome. Activated by ",
            { code: "return_orchestration_enabled" },
            " in ",
            { code: "EZCount Settings" },
            " (see ",
            { href: "#enabling-the-flow", text: "Enabling the flow" },
            ").",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "New ",
              { strong: "B2B Return" },
              " — Desk ",
              { code: "/app/b2b-return/new" },
              "; link the source order and pick the grain.",
            ],
            [
              "Pick the outcome: ",
              { strong: "Monetary Credit" },
              " (spawns a 330), ",
              { strong: "Physical Pickup" },
              " (a ToDo; the credit waits for the goods-receipt DN), or ",
              { strong: "Supplier" },
              " (a Purchase Receipt, no EZCount).",
            ],
            "Submit — on the Monetary Credit path the 330 issues through the same engine as Option 1.",
          ],
        },
        {
          kind: "paragraph",
          text: [
            { strong: "Where & links: " },
            "Desk ",
            { code: "/app/b2b-return" },
            "; flag in ",
            { code: "/app/ezcount-settings" },
            " · see the ",
            { href: "#flow-at-a-glance", text: "flow diagram" },
            " and ",
            { href: "#enabling-the-flow", text: "Enabling the flow" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "use-credit-adjustment",
          text: "Option 3 · B2B Credit Adjustment",
        },
        {
          kind: "paragraph",
          text: [
            "For corrections rather than physical returns: per-line partial credit, ",
            { strong: "cancel & reissue" },
            " (fix a wrong invoice), or re-charge. Activated by ",
            { code: "credit_mechanics_enabled" },
            " in ",
            { code: "EZCount Settings" },
            ".",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            [
              "New ",
              { strong: "B2B Credit Adjustment" },
              " — Desk ",
              { code: "/app/b2b-credit-adjustment/new" },
              "; link the invoice/order and choose the operation.",
            ],
            "Enter the per-line amounts to credit (the ledger enforces a cumulative per-line ceiling).",
            "Submit — a 330 (partial or full reversal) issues, and cancel & reissue also produces a corrected forward invoice.",
          ],
        },
        {
          kind: "paragraph",
          text: [
            { strong: "Where & links: " },
            "Desk ",
            { code: "/app/b2b-credit-adjustment" },
            "; flag in ",
            { code: "/app/ezcount-settings" },
            " · see ",
            { href: "#enabling-the-flow", text: "Enabling the flow" },
            " and ",
            { href: "#how-a-330-is-issued", text: "How a 330 reaches EZCount" },
            ". Start with ",
            { strong: "Partial Credit" },
            " (one 330 against one 305) to validate the round-trip.",
          ],
        },
        {
          kind: "heading",
          id: "use-card-refund",
          text: "Option 4 · Card refund (immediate — apps/api)",
        },
        {
          kind: "paragraph",
          text: [
            "For an immediate/card order, the money is returned to the card on the ",
            { strong: "payment side" },
            " — separate from the 330 accounting credit, and immediate-debit (out of obligo). This runs in ",
            { code: "apps/api" },
            " (→ HYP ",
            { code: "refundDeal" },
            "), not on the ERPNext desk; wiring the return → refund bridge is in-review.",
          ],
        },
        {
          kind: "list",
          ordered: true,
          items: [
            "The credit decision still starts from a return/adjustment above (the business authorises it).",
            [
              "For a card order the refund is to be executed (once the bridge lands) via the ",
              { code: "apps/api" },
              " payments service (HYP ",
              { code: "refundDeal" },
              "), keyed to the original capture.",
            ],
            "The refund does not touch obligo (immediate-debit); no 330 is required for the card leg.",
          ],
        },
        {
          kind: "paragraph",
          text: [
            { strong: "Where & links: " },
            "code in ",
            { code: "apps/api" },
            " (payments); the return → card-refund bridge is tracked on the in-review ",
            { code: "BF-742" },
            " / ",
            { code: "BF-803" },
            " stack. See the ",
            { href: "?page=erpnext-customizations", text: "Customizations" },
            " page for how the desk side is wired.",
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "EZCount has no cancel endpoint",
          text: "A wrong 330 is never reversed — it is corrected by cancel & reissue (a fresh corrected document). Treat an issued 330 as final. Two behaviours are worth confirming against the demo endpoint on first use: the 330→original-invoice link key and the credit sign — validate them with a Partial Credit round-trip.",
        },
      ],
    },
  ],
};
